import {SearchOutlined} from '@ant-design/icons';
import {Button, Input, Modal, Space, Tag} from 'antd';
import moment from 'moment';
import {useMutation, useQueryClient} from 'react-query';
import api from '../../api';
import CRUD from '../../components/CRUD';
import {formatDate} from '../../utils/normalize';
import MemberForm from './MemberForm';
import MemberSummary from './MemberSummary';

const MemberList = ({searchParams}) => {
  let searchInput;
  const queryClient = useQueryClient();
  const listSearchParams = {...searchParams, include_invitations: true};
  const responseStatusMeta = {
    confirmed: {color: 'green', label: 'Teilnahme bestätigt'},
    waitlist: {color: 'gold', label: 'Warteliste'},
    declined: {color: 'volcano', label: 'Abgesagt'},
    open: {color: 'default', label: 'Noch keine Rückmeldung'},
  };

  const getColumnSearchProps = dataIndex => ({
    filterDropdown: ({setSelectedKeys, selectedKeys, confirm, clearFilters}) => (
      <div style={{padding: 8}}>
        <Input
          ref={node => {
            searchInput = node;
          }}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => (selectedKeys, confirm, dataIndex) => confirm()}
          style={{marginBottom: 8, display: 'block'}}
        />
        <Space>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({closeDropdown: false});
            }}
          >
            Filter
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => <SearchOutlined
      style={{color: filtered ? '#1890ff' : undefined}}/>,
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '',
    onFilterDropdownVisibleChange: visible => {
      if (visible) {
        setTimeout(() => searchInput.select(), 100);
      }
    },
  });


  const columns = [
    {
      title: 'Frau/Herr',
      dataIndex: 'greeting',
      key: 'greeting',
    },
    {
      title: 'Titel',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Nachname',
      dataIndex: 'last_name',
      key: 'last_name',
      ...getColumnSearchProps('last_name'),
    },
    {
      title: 'Vorname',
      dataIndex: 'first_name',
      key: 'first_name',
      ...getColumnSearchProps('first_name'),
    },
    {
      title: 'Ort',
      dataIndex: 'area',
      key: 'area',
      sorter: (a, b) => a.area.localeCompare(b.area),
    },
    {
      title: 'Anmeldung',
      dataIndex: 'date_create',
      key: 'date_create',
      render: (text) => {
        return formatDate(text);
      },
      sorter: (a, b) => (a.date_create ? moment(a.date_create).unix() : 0) - (b.date_create ? moment(b.date_create).unix() : 0),
    },
    {
      title: 'Rückmeldung',
      dataIndex: 'response_status',
      key: 'response_status',
      render: (text, row) => {
        const meta = responseStatusMeta[text] || {color: 'default', label: row.response_status_label || text};
        return <Tag color={meta.color}>{row.response_status_label || meta.label}</Tag>;
      },
      sorter: (a, b) => (a.response_status_label || '').localeCompare(b.response_status_label || ''),
    },
    {
      title: 'Status',
      dataIndex: 'status_name',
      key: 'status_name',
      width: '20%',
      sorter: (a, b) => a.status_name.localeCompare(b.status_name),
    },
    {
      title: 'Bestätigung',
      dataIndex: 'confirmation_email_date_sent',
      key: 'confirmation_email',
      render: (text, row) => {
        const sentAt = text ? formatDate(text, 'YYYY-MM-DD HH:mm:ss', 'DD.MM.YYYY HH:mm') : '';
        const hasExtra = !!row.confirmation_email_extra_email;
        const details = [
          row.confirmation_email_member_email && `Teilnehmer: ${row.confirmation_email_member_email}`,
          row.confirmation_email_extra_email && `Zusatz: ${row.confirmation_email_extra_email}`,
          row.confirmation_email_status && `Status: ${row.confirmation_email_status}`,
          row.confirmation_email_error && `Fehler: ${row.confirmation_email_error}`,
        ].filter(Boolean).join('\n');
        if (row.confirmation_email_error) {
          return <a onClick={() => Modal.info({
            title: 'Bestätigungsversand',
            content: <pre style={{whiteSpace: 'pre-wrap'}}>{details}</pre>,
          })}>Fehler</a>;
        }
        if (!sentAt) {
          return '';
        }
        return <a onClick={() => Modal.info({
          title: 'Bestätigungsversand',
          content: <pre style={{whiteSpace: 'pre-wrap'}}>{details}</pre>,
        })}>
          Versendet · {sentAt}{hasExtra ? ' · Zusatz' : ''}
        </a>;
      },
    },
  ];

  const normalizeFormData = data => {
    if (data.evening_programs) {
      data.evening_programs = data.evening_programs.map((program) => ({
        ...program,
        guest_member_count: program.guest_member_count ? 1 : 0,
      }));
    }
    if (data.id) {
      if (data.special_programs.length === 0) {
        delete data.special_programs;
      }
      if (data.accompany_programs.length === 0) {
        delete data.accompany_programs;
      }
      if (data.evening_programs.length === 0) {
        delete data.evening_programs;
      }
      if (data.parking_passes.length === 0) {
        delete data.parking_passes;
      }
    }
    return data;
  }

  const {mutateAsync: memberSendEmail} = useMutation(api.member.memberSendEmail);
  const {mutateAsync: memberPromoteFromWaitlist} = useMutation(api.member.memberPromoteFromWaitlist);

  return <CRUD
    keyName="member"
    itemName="teilnehmer"
    itemValueName="last_name"
    modalWidth="90%"
    FormComponent={MemberForm}
    tableColumns={columns}
    apiList={api.member.memberList}
    apiRetrieve={api.member.memberRetrieve}
    apiCreate={api.member.memberCreate}
    apiUpdate={api.member.memberUpdate}
    apiDelete={api.member.memberDelete}
    searchParams={listSearchParams}
    extraFields={{meeting: searchParams['meeting']}}
    normalizeFormData={normalizeFormData}
    enableBulkDelete
    isRowActionDisabled={(record) => record.can_edit === false || !record.member_id}
    hidePaginationSizeChanger
    renderTopContent={({listData, pageSize, pageSizeOptions, onPageSizeChange}) => (
      <MemberSummary
        searchParams={searchParams}
        members={listData}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        onPageSizeChange={onPageSizeChange}
      />
    )}
    childrenActions={{
      'resendEmail': {
        childrenActionName: 'E-Mail erneut senden',
        onClick: (record) => Modal.confirm({
            title: 'E-Mail erneut senden',
            content: `Senden ${record.last_name}?`,
            onOk: async () => {
              await memberSendEmail(record.id);
              await queryClient.invalidateQueries('member-list');
            },
          }),
      },
      'promoteFromWaitlist': {
        childrenActionName: 'Als Teilnehmer übernehmen',
        onClick: (record) => {
          if (record.registration_state !== 'waitlist') {
            Modal.info({
              title: 'Als Teilnehmer übernehmen',
              content: 'Diese Aktion ist nur für Wartelistenpersonen verfügbar.',
            });
            return;
          }
          Modal.confirm({
            title: 'Als Teilnehmer übernehmen',
            content: `${record.first_name} ${record.last_name} als regulären Teilnehmer übernehmen?`,
            onOk: async () => {
              await memberPromoteFromWaitlist(record.id);
              await queryClient.invalidateQueries('member-list');
            },
          });
        },
      }
    }}
  />
}

export default MemberList;
